/**
 * Listen for children events in a parent element.
 *
 * @example
 *  // listen for clicks of all `li` elements that have the 'is-active' class
 *  delegateEvent(ulElement, 'click', 'li.is-active', callback)
 *
 * @param {Element} rootElement -
 * @param {string} eventName - name of the event to listen to
 * @param {string} selector - selector to filter the elements that trigger the event
 * @param {Function} handler - callback for event
 * @returns {Function} - function to call to event listener
 */
export function delegateEvent(
  rootElement: Element,
  eventName: string,
  selector: string,
  handler: (elem: Element, event: Event) => void
): () => void {
  function delegationHandler(event: Event) {
    const elem = (event.target as HTMLElement).closest(selector);
    if (elem) {
      handler(elem, event);
    }
  }

  return addDisposableEventListener(
    rootElement,
    eventName,
    delegationHandler,
    false
  );
}

/**
 * Attach an event listener to the given element and returns a "dispose" function to call to remove the event listener.
 *
 * This simplifies removing event listeners so you only have to keep a reference to the dispose function and call when you
 * want to unregister the listener.
 *
 * @example normally:
 * const referenceToHandler = function() {...}
 * event.addEventListener('click', referenceToHandler, { useCapture: true });
 * // later (has to pass exact same parameters)
 * event.removeEventListener('click', referenceToHandler, { useCapture: true });
 *
 * @example with addDisposableEventListener:
 * const disposeListener = addDisposableEventListener(event, 'click', function(){...}, { useCapture: true });
 * // later
 * disposeListener();
 *
 * @param {EventTarget} element - Element to attach the event listener to
 * @param  {...any} listenerParams - Same as Element.addEventListener()
 */
export function addDisposableEventListener(
  element: EventTarget,
  ...listenerParams: Parameters<Element['addEventListener']>
): () => void {
  element.addEventListener(...listenerParams);
  return function disposeListener() {
    element.removeEventListener(...listenerParams);
  };
}

/**
 * Implementation of an EventTarget-like interface so components can dispatch custom events.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/EventTarget
 */
export class EventDispatcher<EventMap> {
  private listeners: { [k in keyof EventMap]?: ((ev: EventMap[k]) => void)[] };

  constructor() {
    this.listeners = {};
  }

  addEventListener<K extends keyof EventMap>(
    eventName: K,
    listener: (ev: EventMap[K]) => void
  ): void {
    if (!this.listeners[eventName]) {
      this.listeners[eventName] = [];
    }
    this.listeners[eventName].push(listener);
  }

  removeEventListener<K extends keyof EventMap>(
    eventName: K,
    listener: (ev: EventMap[K]) => void
  ): void {
    if (this.listeners[eventName]) {
      this.listeners[eventName] = this.listeners[eventName].filter(
        (cb) => cb !== listener
      );
    }
  }

  dispatchEvent<K extends keyof EventMap>(
    eventName: K,
    event: EventMap[K]
  ): void {
    if (this.listeners[eventName]) {
      this.listeners[eventName].forEach((listener) => listener(event));
    }
  }

  clearAll(): void {
    this.listeners = {};
  }
}

// https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
const isDomElement = (v: unknown): v is HTMLElement =>
  !!v && (v as Element).nodeType === 1;
/**
 * Check if a value is a DOMElement if it's not then query for an element using the default selector.
 *
 * @param {any|Element} maybeElem - Value that could be a DOMElement
 * @param {object} otherwise - options to query fo the default value
 * @param {Element} otherwise.parent - parent to query for the default element
 * @param {string} otherwise.selector - selector to query for the default element
 * @param {string} [otherwise.throwIfNotFound] - if element not found throw an exception with this message
 */
export function useElementOrFindIt(
  maybeElem: unknown,
  otherwise: { parent: Element; selector: string; throwIfNotFound?: string }
): HTMLElement {
  if (isDomElement(maybeElem)) {
    return maybeElem;
  }
  const { parent, selector, throwIfNotFound } = otherwise;
  const element = parent.querySelector<HTMLElement>(selector);
  if (!element && !!throwIfNotFound) {
    throw new Error(throwIfNotFound);
  }
  return element;
}

/**
 * Query for an element in  a parent, if not found throw an error
 */
export function queryElementOrThrow({
  parent,
  selector,
  throwIfNotFound,
}: {
  parent: Element;
  selector: string;
  throwIfNotFound?: string;
}): HTMLElement {
  const element = parent.querySelector<HTMLElement>(selector);
  if (!element && !!throwIfNotFound) {
    throw new Error(throwIfNotFound);
  }
  return element;
}

/**
 * Toggle a boolean attribute in a given DOM element.
 * 
 * For example for the 'aria-expanded' attribute.
 * 
 * Useful because DOM attributes use 'true' and 'false'
 * as strings and not as primitive boolean values

 *
 * @param {Element} elem - DOM Element
 * @param {string} attributeName - Name of the attribute to toggle
 */
export function toggleBooleanDOMAttr(
  elem: Element,
  attributeName: string
): void {
  let attributeValue = elem.getAttribute(attributeName);
  attributeValue = attributeValue === 'true' ? 'false' : 'true';
  elem.setAttribute(attributeName, attributeValue);
}
